import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneView,
} from 'lightweight-charts';

/**
 * Fires a callback when the PRICE scale changes.
 *
 * lightweight-charts v5 exposes no price-scale subscription — only
 * `timeScale().subscribeVisibleLogicalRangeChange`. Anything positioned with
 * `priceToCoordinate()` therefore has no way to learn that the vertical mapping
 * moved, which is how the chart's DOM overlays (alert lines, the draft bracket)
 * ended up frozen at a stale y after a vertical drag or an autoscale.
 *
 * A primitive gets `updateAllViews()` on every chart redraw, including redraws
 * caused by a price-scale change, so it can act as the subscription the library
 * doesn't offer. This one draws nothing — it samples where a fixed probe price
 * lands and reports when that moves.
 *
 * Two properties matter for safety:
 *
 *  - It only fires when the coordinate actually changed. A React re-render
 *    triggered by the callback does not move the price scale, so the next
 *    `updateAllViews()` sees the same coordinate and stops. No feedback loop.
 *
 *  - The callback is debounced. During a drag the scale moves every frame, and
 *    the consumer is a re-render of a very large component.
 */
export class PriceScaleWatcherPrimitive implements ISeriesPrimitive<Time> {
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _onChange: () => void;
  private _debounceMs: number;
  private _lastCoordinate: number | null = null;
  private _timer?: ReturnType<typeof setTimeout>;

  constructor(onChange: () => void, debounceMs = 100) {
    this._onChange = onChange;
    this._debounceMs = debounceMs;
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._lastCoordinate = null;
  }

  detached(): void {
    if (this._timer) clearTimeout(this._timer);
    this._timer = undefined;
    this._series = undefined;
    this._lastCoordinate = null;
  }

  paneViews(): IPrimitivePaneView[] {
    return [];
  }

  updateAllViews(): void {
    if (!this._series) return;

    // The probe price is arbitrary — any fixed price works, because the question
    // is only whether the price→pixel mapping moved, not what it maps to. It is
    // read from the series' own current data so it stays inside the visible
    // range, where priceToCoordinate is meaningful.
    const probe = this._probePrice();
    if (probe == null) return;

    const y = this._series.priceToCoordinate(probe);
    if (y == null) return;

    if (this._lastCoordinate !== null && Math.abs(y - this._lastCoordinate) < 0.5) {
      return; // scale has not meaningfully moved
    }

    const first = this._lastCoordinate === null;
    this._lastCoordinate = y;
    if (first) return; // establishing the baseline is not a change

    if (this._timer) clearTimeout(this._timer);
    this._timer = setTimeout(() => this._onChange(), this._debounceMs);
  }

  private _probePrice(): number | null {
    const data = this._series?.data?.();
    if (!data || data.length === 0) return null;
    const last = data[data.length - 1] as { close?: number; value?: number };
    return last.close ?? last.value ?? null;
  }
}

export function createPriceScaleWatcherPrimitive(
  onChange: () => void,
  debounceMs = 100
): PriceScaleWatcherPrimitive {
  return new PriceScaleWatcherPrimitive(onChange, debounceMs);
}
